import React, { createContext, useContext } from 'react';

import { SessionData } from '@eluve/api-contract';

/**
 * Session data decoded from the tab's MINTED tenant-scoped JWT (ENG-5545).
 *
 * Since tenant tokens became per-tab, the shared SuperTokens session's claims
 * (`x-hasura-tenant-id`, `x-hasura-allowed-roles`, `x-hasura-default-role`)
 * freeze at their login-time tenant and no longer describe the tenant a tab is
 * on. A user who is a member in tenant A and an admin in tenant B must be
 * evaluated with B's role while on B — the same claims their GraphQL requests
 * carry. TenantGuard provides the minted token's payload here; `useSession`
 * prefers it over the shared session whenever it's present, so every
 * role/tenant hook under a tenant route is correct without call-site changes.
 *
 * Null outside tenant routes (and during the legacy shared-session fallback,
 * where the shared claims ARE the tab's claims).
 */
const TenantScopedSessionContext = createContext<SessionData | null>(null);

export const TenantScopedSessionProvider: React.FC<{
  value: SessionData | null;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <TenantScopedSessionContext.Provider value={value}>
    {children}
  </TenantScopedSessionContext.Provider>
);

export const useTenantScopedSessionData = (): SessionData | null =>
  useContext(TenantScopedSessionContext);
